import { html, nothing } from 'lit';
import { templateContent } from 'lit/directives/template-content.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { TefComsCustomerAttributeBase } from '@common/customerAttributes/TefComsCustomerAttributeBase.js';
import { CustomerAttribute, CustomerAttributeGroupItem } from '@common/customerAttributes/types.js';
import { hasContent } from '@common/lit/hasContent.js';
import { removeHTML } from '@common/lit/removeHTML.js';
import type { TefComsCustomerAttributeInline } from '@src/components/tef-coms-customer-attribute-inline.js';
import type { TefComsCustomerAttribute } from '@src/components/tef-coms-customer-attribute.js';
import { localDate, localTime } from '@src/components/templates/util.js';

export function getCustomerAttributesWithBulletpointTemplateResult(customerAttribute: CustomerAttribute, type: 'standard' | 'extra') {
  return customerAttribute[type]!.items.map((item: CustomerAttributeGroupItem) => html`
    <li class="coms-consent">${unsafeHTML(item.title)}</li>
  `);
}

export function getCustomerAttributesWithIconTemplateResult(customerAttribute: CustomerAttribute) {
  // Here granted=undefined should NOT be displayed
  return customerAttribute.extra!.items.filter((item) => item.display).map((item: CustomerAttributeGroupItem) => html`
    <li><tef-icon icon="${item.granted ? 'check' : 'cross'}"></tef-icon>${unsafeHTML(item.title)}</li>`);
}

export function getCustomerAttributesWithCheckboxTemplateResult(customerAttribute: CustomerAttribute) {
  return customerAttribute.extra!.items.map((item: CustomerAttributeGroupItem) => html`
    <div class="form-group-checkbox form-group-checkbox--size-small">
      <input id=${item.id} type="checkbox" .checked=${item.granted} data-cattr-id="${customerAttribute.id}" data-cattr-item-id="${item.id}"></input>
      <label for=${item.id}>${unsafeHTML(item.title)}</label>
    </div>
  `);
}

export function getExtraCustomerAttributeItemsTemplateResult(customerAttribute: CustomerAttribute, itemsDisplayStyle: string) {
  if (!customerAttribute.extra) return [];

  const blocks = [html`<p><em>${unsafeHTML(customerAttribute.extra.title || 'Zusatzprodukte')}</em></p>`];

  switch (itemsDisplayStyle) {
    case 'bulletpoint':
      blocks.push(html`<ul class="list" aria-label="Zusatzprodukte">${getCustomerAttributesWithBulletpointTemplateResult(customerAttribute, 'extra')}</ul>`);
      break;
    case 'icon':
      blocks.push(html`<ul class="list--icon">${getCustomerAttributesWithIconTemplateResult(customerAttribute)}</ul>`);
      break;
    case 'checkbox':
      blocks.push(html`<div class="form-row form-row--group">${getCustomerAttributesWithCheckboxTemplateResult(customerAttribute)}</div>`);
      break;
    default:
  }

  return blocks;
}

export function getLastChangeTemplateResult(customerAttribute: CustomerAttribute, itemsDisplayStyle: string) {
  if (itemsDisplayStyle !== 'bulletpoint' && customerAttribute.date) {
    return html`
      <p>
        <small>
          <tef-coms-icon icon="clock"></tef-coms-icon>
          Letzte Änderung am ${localDate(customerAttribute.date)} um ${localTime(customerAttribute.date)} Uhr
        </small>
      </p>
    `;
  }
  return null;
}

export function getButtonsTemplateResult(tefComsCustomerAttribute: TefComsCustomerAttributeBase, itemsDisplayStyle: string) {
  if (itemsDisplayStyle === 'checkbox') {
    return html`
      <div class="section-buttons action-primary" slot="action-primary">
        <button class="button button-default" data-modal-close @click=${() => { tefComsCustomerAttribute.save(); }}>Weiter</button>
      </div>
      <div class="section-buttons action-secondary" slot="action-secondary">
        <button class="button button-border" data-modal-close @click=${() => { tefComsCustomerAttribute.cancel(); }}>Abbrechen</button>
      </div>
    `;
  }
  return html`
   <div class="section-buttons action-primary" slot="action-secondary">
      <button class="button button-default" data-modal-close>Zurück</button>
    </div>
  `;
}

//
// Display the modal dialog
//
export const tefComsCustomerAttributeModalDialog = function render(
  this: TefComsCustomerAttributeBase,
  customerAttribute: CustomerAttribute,
  itemsDisplayStyle: string,
  template$: HTMLTemplateElement,
) {
  const title = customerAttribute.title || template$.content.textContent || '';

  return html`
    <tef-modal id="${this.modalId}" role="alertdialog" aria-label="Weitere Informationen und Einstellungen zu '${removeHTML(title)}'" cloak aria-modal="true">
      <div slot="title">${unsafeHTML(title)}</div>
      <div slot="body">
        ${hasContent(customerAttribute.header) ? html`<p data-testid="cattr-header">${unsafeHTML(customerAttribute.header)}</p>` : nothing}
        <p><em>${unsafeHTML(customerAttribute.standard.title || 'Standardprodukte')}</em></p>
        <ul class="list" aria-label="Standard Produkte">${getCustomerAttributesWithBulletpointTemplateResult(customerAttribute, 'standard')}</ul>
        ${getExtraCustomerAttributeItemsTemplateResult(customerAttribute, itemsDisplayStyle)}
        ${getLastChangeTemplateResult(customerAttribute, itemsDisplayStyle)}
        ${hasContent(customerAttribute.footer) ? html`<p data-testid="cattr-footer">${unsafeHTML(customerAttribute.footer)}</p>` : nothing}
      </div>
      ${getButtonsTemplateResult(this, itemsDisplayStyle)}
    </tef-modal>
  `;
};

//
// Render trigger for modal dialog
//
// The single <a> is sadly needed to format this thing with bold text :(
export const tefComsCustomerAttributeModalTrigger = function render(
  this: TefComsCustomerAttribute,
  template$: HTMLTemplateElement,
) {
  return html`
    <tef-modal-trigger
      modal-id="tef-coms-customer-attribute-${this.id}"
      role="button"
      aria-haspopup="dialog"
      aria-controls="tef-coms-customer-attribute-${this.id}"
      aria-label="${removeHTML(this.customerAttribute!.title)}"
    >
      <a>${templateContent(template$)}</a>
    </tef-modal-trigger>`;
};

//
// Displays ONE customer attributes inline instead of in a modal dialog
//
export const tefComsCustomerAttributeInline = function render(
  this: TefComsCustomerAttributeInline,
  customerAttribute: CustomerAttribute,
) {
  const checkboxesHtml = customerAttribute.extra!.items.map((item: CustomerAttributeGroupItem) => html`
    <div class="form-group-checkbox form-group-checkbox--size-small">
      <input id=${item.id} type="checkbox" .checked=${item.granted} data-cattr-id="${customerAttribute.id}" data-cattr-item-id="${item.id}" ?readonly="${!item.display}" ?disabled="${!item.display}"></input>
      <label for=${item.id}>${unsafeHTML(item.title)}</label>
    </div>
  `);

  return html`
    <div id="tef-coms-customer-attribute-inline">
      ${hasContent(customerAttribute.title) ? unsafeHTML(`<h3 data-testid="cattr-title">${customerAttribute.title}</h3>`) : nothing}
      ${hasContent(customerAttribute.header) ? html`<p data-testid="cattr-header">${unsafeHTML(customerAttribute.header)}</p>` : nothing}
      <p><em>${unsafeHTML(customerAttribute.standard.title || 'Standardprodukte')}</em></p>
      <ul class="list">${getCustomerAttributesWithBulletpointTemplateResult(customerAttribute, 'standard')}</ul>
      <p><em>${unsafeHTML(customerAttribute.extra!.title || 'Zusatzprodukte')}</em></p>
      <div class="form-row form-row--group">${checkboxesHtml}</div>
      <hr>
      ${getLastChangeTemplateResult(customerAttribute, 'checkbox')}
      ${hasContent(customerAttribute.footer) ? html`<p data-testid="cattr-footer">${unsafeHTML(customerAttribute.footer)}</p>` : nothing}
      <div class="form-row--buttons">
        <button data-testid="button-cancel" class="button button-border" @click=${() => { this.cancel(); }}>Abbrechen</button>
        <button data-testid="button-save" class="button button-default" @click=${() => { this.save(); }} ?disabled=${!this.customerAttributeItemsStatusChanged}>Speichern</button>
      </div>
    </div>
  `;
};
